import { css } from '@linaria/core';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { AutoComplete, Input, InputRef, Select, Space } from 'antd';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

import { catalogPreviewOptions, gamesOptions } from '@lib/api/options/catalog';
import { useDebouncedValue } from '@lib/hooks/use-debounced-value';
import { Game } from '@lib/types';
import { ROUTE_PATHS } from '@modules/route-paths';

import CardSearchOption from '../../../card-search-option';

interface Props {
  className?: HTMLDivElement['className'];
  expanded?: boolean;
  onNavigate?: () => void;
}

const styles = {
  search: css`
    width: 100%;

    .ant-input-group-addon {
      padding: 0;
      border: 0;
    }

    * {
      height: min-content;

      max-height: 36px;

      .ant-select-content::before {
        content: none;
      }
    }
  `,
  container: css`
    width: 100%;
    max-width: 420px;
    min-width: 0;
    max-height: 36px;

    .ant-input,
    .ant-select-selector {
      background: var(--ant-color-fill-secondary);
    }
  `,
  game: css`
    width: 110px;
    flex-shrink: 0;
  `,
  searchInput: css`
    width: 100%;
    min-width: 0;
  `,
  dropdown: css`
    width: min(480px, calc(100vw - 2 * var(--ant-padding-sm)));
    max-width: calc(100vw - 2 * var(--ant-padding-sm));
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;

    .ant-select-item-option-content {
      touch-action: pan-y;
    }
  `,
};

const CARD_PREVIEW_DELAY = 300;
const MINIMUM_CARD_PREVIEW_LENGTH = 2;

const GlobalCardSearch = ({
  className,
  expanded = false,
  onNavigate,
}: Props) => {
  const searchInput = useRef<InputRef>(null);

  useEffect(() => {
    if (expanded) {
      searchInput.current?.focus();
    }
  }, [expanded]);

  const navigate = useNavigate();
  const location = useLocation();

  const routeGameCode = location.pathname.match(
    /^\/([^/]+)\/cards(?:\/|$)/,
  )?.[1];

  const [selectedGameCode, setGameCode] = useState<Game['slug']>(
    routeGameCode ?? '',
  );

  const { data: games } = useSuspenseQuery(gamesOptions());

  const preferredGameCode = routeGameCode ?? selectedGameCode;
  const gameCode = games.some((game) => game.slug === preferredGameCode)
    ? preferredGameCode
    : games[0]?.slug;

  const [search, setSearch] = useState('');

  const debouncedSearch = useDebouncedValue(search.trim(), CARD_PREVIEW_DELAY);

  const { data: preview = [], isFetching } = useQuery({
    ...catalogPreviewOptions(debouncedSearch, gameCode),
    enabled:
      Boolean(gameCode) &&
      debouncedSearch.length >= MINIMUM_CARD_PREVIEW_LENGTH,
  });

  const onSearchCard = (search: string) => {
    if (!gameCode) {
      return;
    }

    onNavigate?.();
    navigate({
      to: ROUTE_PATHS.CARDS,
      params: { gameCode },
      search: { search: search.trim() },
    }).catch(() => undefined);
  };

  const onSelectGame = (nextGameCode: string) => {
    setGameCode(nextGameCode);
    navigate({
      to: ROUTE_PATHS.CARDS,
      params: { gameCode: nextGameCode },
      search: {},
    }).catch(() => undefined);
  };

  const onSelectCard = (cardSlug: string) => {
    if (!gameCode) {
      return;
    }

    setSearch('');
    onNavigate?.();
    navigate({
      to: ROUTE_PATHS.CARD_DETAILS,
      params: { cardSlug, gameCode },
    }).catch(() => undefined);
  };

  const options = preview.map((card) => ({
    value: card.slug,
    label: (
      <CardSearchOption
        imageUrl={card.imageUrl}
        nameEn={card.nameEn}
        namePt={card.namePt}
      />
    ),
  }));

  return (
    <Space.Compact className={classNames(styles.container, className)}>
      <Select
        className={styles.game}
        aria-label="Selecionar TCG"
        value={gameCode}
        disabled={!games.length}
        placeholder="Nenhum TCG disponível"
        options={games.map((game) => ({
          label: game.name,
          value: game.slug,
        }))}
        onChange={onSelectGame}
        variant="filled"
      />
      <AutoComplete
        popupMatchSelectWidth={false}
        popupAlign={{ overflow: { adjustX: 1, adjustY: 1, shiftX: true } }}
        disabled={!gameCode}
        className={styles.search}
        value={search}
        options={options}
        notFoundContent={
          debouncedSearch.length >= MINIMUM_CARD_PREVIEW_LENGTH && !isFetching
            ? 'Nenhuma carta encontrada'
            : null
        }
        onChange={setSearch}
        onSelect={onSelectCard}
        classNames={{
          popup: {
            root: styles.dropdown,
          },
        }}
      >
        <Input.Search
          ref={searchInput}
          className={styles.searchInput}
          aria-label="Buscar cartas no marketplace"
          loading={isFetching}
          disabled={!gameCode}
          placeholder="Buscar carta"
          onSearch={onSearchCard}
        />
      </AutoComplete>
    </Space.Compact>
  );
};

export default GlobalCardSearch;
